import { useState, useCallback } from 'react';

const CLOSE_DELAY_MS = 170;

/**
 * Hook for managing DriveWorkspace actions and modal states
 * Handles all user interactions: share, duplicate, rename, delete, assign agent, etc.
 * 
 * @param {Object} options - Hook options
 * @param {Object} options.storeRef - Ref to the workspace store
 * @param {Function} options.toast - Toast notification function
 * @param {string} options.userId - Current user ID
 * @param {Function} options.navigate - Navigation function
 * @param {Function} options.openFileSelector - Function to open file selector
 * @returns {Object} - Modal states and action handlers
 */
export const useDriveWorkspaceActions = ({
  storeRef,
  toast,
  userId,
  navigate,
  openFileSelector,
}) => {
  // Document detail slideout state
  const [isDocumentDetailOpen, setIsDocumentDetailOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Folder info slideout state
  const [isFolderInfoOpen, setIsFolderInfoOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Rename folder modal state
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);

  // Rename document modal state
  const [isRenameDocumentModalOpen, setIsRenameDocumentModalOpen] = useState(false);
  const [documentToRename, setDocumentToRename] = useState(null);

  // Alert state
  const [alert, setAlert] = useState(null);

  // New folder modal state
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);

  // ===== Share Modal Handlers =====
  const handleShareModalOpen = useCallback((item, type) => {
    const currentStore = storeRef.current;
    const result = currentStore.openShareModal(item, type);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, toast]);

  const handleBannerShare = useCallback(() => {
    const currentStore = storeRef.current;
    const selectedDocuments = currentStore.selectionStore.selectedDocuments;
    const selectedFolders = currentStore.selectionStore.selectedFolders;

    if (selectedDocuments.length > 0 || selectedFolders.length > 0) {
      const virtualItem = {
        type: selectedFolders.length > 0 ? 'folder' : 'document',
        name: `${selectedDocuments.length + selectedFolders.length} items selected`,
      };
      handleShareModalOpen(virtualItem, 'mixed');
    }
  }, [storeRef, handleShareModalOpen]);

  const handleAddToPending = useCallback(() => {
    const currentStore = storeRef.current;
    const result = currentStore.addToPendingGrantees();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, toast]);

  const handleShareAll = useCallback(async () => {
    const currentStore = storeRef.current;
    const result = await currentStore.shareAllPendingGrantees();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, toast]);

  const handleRemoveAccess = useCallback(async (granteeUuid, granteeType) => {
    const currentStore = storeRef.current;
    const result = await currentStore.removeAccessFromItem(granteeUuid, granteeType);

    if (result) {
      const variant = result.variant || (result.success ? 'success' : 'error');
      const title = result.title || (result.success ? 'Success' : 'Error');
      const message = result.message || (result.success ? 'Operation completed' : 'Operation failed');
      
      toast[variant]({
        title: title,
        message: message,
      });
    }
  }, [storeRef, toast]);

  const handleUpdatePermission = useCallback(async (granteeUuid, granteeType, newPermission) => {
    const currentStore = storeRef.current;
    const result = await currentStore.updatePermissionForGrantee(granteeUuid, granteeType, newPermission);
    
    if (result) {
      const variant = result.variant || (result.success ? 'success' : 'error');
      const title = result.title || (result.success ? 'Success' : 'Error');
      const message = result.message || (result.success ? 'Operation completed' : 'Operation failed');
      
      toast[variant]({
        title: title,
        message: message,
      });
    }
  }, [storeRef, toast]);

  const handleCloseShareModal = useCallback(() => {
    storeRef.current.closeShareModal();
  }, [storeRef]);

  const handleUpdatePendingPermission = useCallback((granteeUuid, newPermission) => {
    storeRef.current.updatePendingGranteePermission(granteeUuid, newPermission);
  }, [storeRef]);

  const handleRemovePending = useCallback((granteeUuid) => {
    storeRef.current.removePendingGrantee(granteeUuid);
  }, [storeRef]);

  // ===== Duplicate Modal Handlers =====
  const handleDuplicateItem = useCallback(async () => {
    const currentStore = storeRef.current;
    const result = await currentStore.duplicateItem();
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, toast]);

  const handleCloseDuplicateModal = useCallback(() => {
    storeRef.current.closeDuplicateModal();
  }, [storeRef]);

  // ===== Assign Agent Modal Handlers =====
  const handleAssignAgentModalOpen = useCallback((item) => {
    const currentStore = storeRef.current;
    const result = currentStore.openAssignAgentModal(item);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, toast]);

  const handleAssignAgent = useCallback(async () => {
    const currentStore = storeRef.current;
    const result = await currentStore.assignAgentsToDocument(userId);
    if (result?.message) {
      toast[result.variant]({
        title: result.title,
        message: result.message,
      });
    }
  }, [storeRef, userId, toast]);

  const handleCloseAssignAgentModal = useCallback(() => {
    storeRef.current.closeAssignAgentModal();
  }, [storeRef]);

  // ===== Move Modal Handlers =====
  const handleBannerMove = useCallback(() => {
    storeRef.current.openMoveModal();
  }, [storeRef]);

  const handleCloseMoveModal = useCallback(() => {
    const itemType = storeRef.current.itemTypeBeingMoved;
    storeRef.current.closeMoveModal();
    toast.success({
      message: `${itemType === 'folder' ? 'Folder' : 'Document'} moved successfully.`,
    });
  }, [storeRef, toast]);

  // ===== Document Detail Slideout Handlers =====
  const handleOpenDocumentDetail = useCallback((document) => {
    setSelectedDocument(document);
    setIsDocumentDetailOpen(true);
  }, []);

  const handleCloseDocumentDetail = useCallback(() => {
    setIsDocumentDetailOpen(false);
  }, []);

  const closeSlideoutThen = useCallback((next) => {
    setIsDocumentDetailOpen(false);
    setTimeout(next, CLOSE_DELAY_MS);
  }, []);

  // ===== Folder Info Slideout Handlers =====
  const handleOpenFolderInfo = useCallback((folder) => {
    setSelectedFolder(folder);
    setIsFolderInfoOpen(true);
  }, []);

  const handleCloseFolderInfo = useCallback(() => {
    setIsFolderInfoOpen(false);
  }, []);

  // ===== Rename Folder Modal Handlers =====
  const handleOpenRenameFolderModal = useCallback((folder) => {
    setFolderToRename(folder);
    setIsRenameFolderModalOpen(true);
  }, []);

  const handleCloseRenameFolderModal = useCallback(() => {
    setIsRenameFolderModalOpen(false);
    setFolderToRename(null);
  }, []);

  const handleSaveRenameFolder = useCallback((result) => {
    if (result.success && result.updatedFolder) {
      const currentStore = storeRef.current;
      const folder = currentStore.folderList.find(f => f.folder_uuid === result.folderUuid);
      if (folder) {
        folder.setName(result.updatedFolder.name);
        currentStore.setFolderInList(folder);
      }
    }
  }, [storeRef]);

  // ===== Rename Document Modal Handlers =====
  const handleOpenRenameDocumentModal = useCallback((document) => {
    setDocumentToRename(document);
    setIsRenameDocumentModalOpen(true);
  }, []);

  const handleCloseRenameDocumentModal = useCallback(() => {
    setIsRenameDocumentModalOpen(false);
    setDocumentToRename(null);
  }, []);

  // Note: handleSaveRenameDocument uses documentToRename from closure
  const handleSaveRenameDocument = useCallback((result) => {
    if (result?.success) {
      const newName = result.newName || result.updatedDocument?.name;
      const documentUuid =
        result.documentUuid ||
        result.updatedDocument?.documentUuid ||
        result.updatedDocument?.document_uuid ||
        documentToRename?.document_uuid ||
        documentToRename?.documentUuid ||
        documentToRename?.id;

      if (documentUuid && newName) {
        const currentStore = storeRef.current;
        const docInStore = currentStore.documentList.find(
          (doc) =>
            doc.document_uuid === documentUuid ||
            doc.documentUuid === documentUuid ||
            doc.id === documentUuid
        );

        if (docInStore) {
          if (typeof docInStore.setName === 'function') {
            docInStore.setName(newName);
          } else {
            docInStore.name = newName;
          }
          currentStore.setDocumentInList(docInStore);
        }
      }
    }
  }, [storeRef, documentToRename]);

  // ===== Delete Document Modal Handlers =====
  const handleDeleteDocument = useCallback((document) => {
    storeRef.current.openDeleteModal(document);
  }, [storeRef]);

  const handleConfirmDelete = useCallback(async () => {
    const currentStore = storeRef.current;
    let result;
    try {
      result = await currentStore.deleteDocument();
    } catch (error) {
      result = { success: false, error: error.message || 'Unexpected error deleting document' };
    }
    
    currentStore.closeDeleteModal();
    
    setTimeout(() => {
      if (result.success) {
        setAlert({
          title: 'Success',
          message: result.message || 'Document deleted successfully',
          severity: 'success'
        });
      } else {
        setAlert({
          title: 'Error',
          message: result.error || result.message || 'Error deleting document',
          severity: 'error'
        });
      }
    }, 100);
  }, [storeRef]);

  const handleCloseDeleteModal = useCallback(() => {
    storeRef.current.closeDeleteModal();
  }, [storeRef]);

  // ===== Alert Handler =====
  const handleCloseAlert = useCallback(() => {
    setAlert(null);
  }, []);

  // ===== New Folder Modal Handlers =====
  const handleOpenNewFolderModal = useCallback(() => {
    setShowNewFolderModal(true);
  }, []);

  const handleCloseNewFolderModal = useCallback(() => {
    setShowNewFolderModal(false);
  }, []);

  const handleCreateFolder = useCallback(() => {
    // Store handles data reload automatically in createFolder
  }, []);

  // ===== Navigation Handlers =====
  const handleUploadDocuments = useCallback(() => {
    openFileSelector();
  }, [openFileSelector]);

  const handleBackToDrives = useCallback(() => {
    navigate('/v2/drives/');
  }, [navigate]);

  return {
    // Document detail slideout
    isDocumentDetailOpen,
    selectedDocument,
    setSelectedDocument,
    handleOpenDocumentDetail,
    handleCloseDocumentDetail,
    closeSlideoutThen,

    // Folder info slideout
    isFolderInfoOpen,
    selectedFolder,
    handleOpenFolderInfo,
    handleCloseFolderInfo,

    // Rename folder modal
    isRenameFolderModalOpen,
    folderToRename,
    handleOpenRenameFolderModal,
    handleCloseRenameFolderModal,
    handleSaveRenameFolder,

    // Rename document modal
    isRenameDocumentModalOpen,
    documentToRename,
    handleOpenRenameDocumentModal,
    handleCloseRenameDocumentModal,
    handleSaveRenameDocument,

    // Alert
    alert,
    handleCloseAlert,

    // New folder modal
    showNewFolderModal,
    handleOpenNewFolderModal,
    handleCloseNewFolderModal,
    handleCreateFolder,

    // Share modal handlers
    handleShareModalOpen,
    handleBannerShare,
    handleAddToPending,
    handleShareAll,
    handleRemoveAccess,
    handleUpdatePermission,
    handleCloseShareModal,
    handleUpdatePendingPermission,
    handleRemovePending,

    // Duplicate modal handlers
    handleDuplicateItem,
    handleCloseDuplicateModal,

    // Assign agent modal handlers
    handleAssignAgentModalOpen,
    handleAssignAgent,
    handleCloseAssignAgentModal,

    // Move modal handlers
    handleBannerMove,
    handleCloseMoveModal,

    // Delete modal handlers
    handleDeleteDocument,
    handleConfirmDelete,
    handleCloseDeleteModal,

    // Navigation handlers
    handleUploadDocuments,
    handleBackToDrives,
  };
};

export default useDriveWorkspaceActions;
